import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileSpreadsheet, FileText, Download, Loader2, ShoppingBag, Wallet } from "lucide-react";
import { toast } from "sonner";

const PERIODS = [{ k: "today", l: "Aujourd'hui" }, { k: "week", l: "Cette semaine" }, { k: "month", l: "Ce mois" }, { k: "custom", l: "Personnalisé" }];

export default function Reports() {
  const [dataset, setDataset] = useState("orders");
  const [period, setPeriod] = useState("month");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [status, setStatus] = useState("all");
  const [busy, setBusy] = useState("");

  const download = async (format) => {
    setBusy(format);
    try {
      const params = new URLSearchParams({ dataset, format, period });
      if (period === "custom") { params.set("date_from", dateFrom); params.set("date_to", dateTo); }
      if (dataset === "orders" && status !== "all") params.set("status", status);
      const res = await api.get(`/reports/export?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `clinit_${dataset}_${period}.${format === "xlsx" ? "xlsx" : format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Rapport téléchargé");
    } catch (e) {
      toast.error("Échec du téléchargement");
    } finally { setBusy(""); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div><h1 className="text-2xl sm:text-3xl font-bold font-heading">Rapports & Exports</h1><p className="text-slate-500 text-sm mt-1">Exportez commandes et encaissements par période</p></div>

      <div className="bg-white border rounded-2xl p-6 space-y-5">
        <div>
          <Label className="mb-2 block">Type de données</Label>
          <div className="grid grid-cols-2 gap-3">
            <button data-testid="report-dataset-orders" onClick={() => setDataset("orders")} className={`flex items-center gap-3 p-4 rounded-xl border text-left ${dataset === "orders" ? "border-brand-500 bg-brand-50" : "border-slate-200"}`}>
              <ShoppingBag className={`w-6 h-6 ${dataset === "orders" ? "text-brand-600" : "text-slate-400"}`} />
              <div><div className="font-semibold">Commandes</div><div className="text-xs text-slate-500">Liste détaillée + montants</div></div>
            </button>
            <button data-testid="report-dataset-payments" onClick={() => setDataset("payments")} className={`flex items-center gap-3 p-4 rounded-xl border text-left ${dataset === "payments" ? "border-brand-500 bg-brand-50" : "border-slate-200"}`}>
              <Wallet className={`w-6 h-6 ${dataset === "payments" ? "text-brand-600" : "text-slate-400"}`} />
              <div><div className="font-semibold">Encaissements</div><div className="text-xs text-slate-500">Paiements enregistrés</div></div>
            </button>
          </div>
        </div>

        <div>
          <Label className="mb-2 block">Période</Label>
          <div className="flex flex-wrap gap-2">
            {PERIODS.map((p) => (
              <button key={p.k} data-testid={`report-period-${p.k}`} onClick={() => setPeriod(p.k)} className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${period === p.k ? "bg-brand-600 text-white border-brand-600" : "bg-white border-slate-200 text-slate-600"}`}>{p.l}</button>
            ))}
          </div>
          {period === "custom" && (
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div><Label className="text-xs">Du</Label><Input data-testid="report-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1" /></div>
              <div><Label className="text-xs">Au</Label><Input data-testid="report-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1" /></div>
            </div>
          )}
        </div>

        {dataset === "orders" && (
          <div>
            <Label className="mb-2 block">Statut</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="report-status" className="w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                {["Nouveau", "Collecté", "Traité", "Dispatché", "Livré"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="border-t pt-5">
          <Label className="mb-2 block">Format d'export</Label>
          <div className="grid sm:grid-cols-3 gap-3">
            <Button data-testid="report-csv" onClick={() => download("csv")} disabled={!!busy} variant="outline" className="h-12">
              {busy === "csv" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-2" /> CSV</>}
            </Button>
            <Button data-testid="report-xlsx" onClick={() => download("xlsx")} disabled={!!busy} className="h-12 bg-emerald-600 hover:bg-emerald-700">
              {busy === "xlsx" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileSpreadsheet className="w-4 h-4 mr-2" /> Excel</>}
            </Button>
            <Button data-testid="report-pdf" onClick={() => download("pdf")} disabled={!!busy} className="h-12 bg-red-600 hover:bg-red-700">
              {busy === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileText className="w-4 h-4 mr-2" /> PDF</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
